import { useTranslation } from 'react-i18next'
import { Globe } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from '@/components/ui/dropdown-menu'
import { LanguageMenuItems } from '@/components/ui/language-menu-items'
import { cn } from '@/lib/utils'
import { placed } from '@/lib/style-guard'

interface Props {
  /** Visual treatment: 'app' for the light app chrome, 'onDark' for dark/public headers. */
  tone?: 'app' | 'onDark'
  className?: string
}

/**
 * End-user language switcher (Norsk / English). Calls i18n.changeLanguage;
 * LanguageProvider persists to the profile (when logged in) and i18next caches
 * to localStorage (for anonymous visitors on public pages). Available in the app
 * topbar and the public attendee header so anyone can pick their language.
 */
export function LanguageSwitcher({ tone = 'app', className }: Props) {
  const { i18n, t } = useTranslation()
  const current = i18n.language?.startsWith('en') ? 'en' : 'nb'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'hit-area inline-flex items-center gap-1.5 rounded-full px-2.5 h-9 text-label transition-colors cursor-pointer focus-ring', // signature-ok: an icon control, not a CTA; round to match the circular icon buttons beside it in AttendeeHeader
            tone === 'onDark'
              ? 'text-[var(--text-public-secondary)] hover:text-[var(--text-public-primary)] hover:bg-[var(--surface-on-dark-hover)]'
              : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-muted)]',
            placed(className, 'LanguageSwitcher'),
          )}
        >
          <Globe className="size-4" aria-hidden="true" />
          {/* The name is the hidden label PLUS the visible code («Bytt språk nb»), never an
              aria-label: an aria-label replaced the visible «nb» and failed WCAG 2.5.3 Label in
              Name (axe `label-content-name-mismatch`) on every public header from `sm` up,
              measured 06.10.2026 on `/`. Below `sm` the code is hidden and the label alone names it. */}
          <span className="sr-only">{t('language.switch')}</span>
          {/* 13px, not 11px. Mounting this in PublicHeader put the trigger on
              every marketing route, and `text-2xs` is under the 12.5px mobile
              floor (22d) — layout-gate caught 18 new findings the moment it
              appeared.

              Hidden below `sm`, and the 18 pixels are the reason. Measured at
              390px on the marketing header: the lockup needs 128px, the right
              cluster 248px, and the content box is 358px — an 18px deficit that
              both sides were paying for by squeezing, which is how the wordmark
              ended up under this button. Dropping the two-letter label frees
              ~21px and the row fits with room to spare. The globe still reads
              as «language» on its own (it is the universal affordance), the
              44px target is unchanged, and the menu that opens names both
              languages with the current one marked — so nothing is lost except
              a label that was restating what the open menu says anyway. */}
          <span className="hidden text-caption sm:inline">{current}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <LanguageMenuItems />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
